import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Trophy, ArrowRight, BarChart3, MessageSquare, Phone, X, Sparkles,
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function FirstWinBanner({ onDismiss }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [milestones, setMilestones] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('a9_first_win_dismissed');
    if (stored === 'true') { setDismissed(true); return; }

    axios.get(`${API}/onboarding/milestones`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(({ data }) => setMilestones(data)).catch(() => {});
  }, [token]);

  if (dismissed || !milestones) return null;

  const { first_simulation_done, first_call_done, first_lead_captured, activation_score } = milestones;
  const allDone = first_simulation_done && first_call_done && first_lead_captured;
  const nothingDone = !first_simulation_done && !first_call_done;

  // Already fully activated — no banner needed
  if (allDone && activation_score >= 75) return null;

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem('a9_first_win_dismissed', 'true');
    onDismiss?.();
  };

  // Show "first win" celebration if just completed first sim or call
  if (first_simulation_done && !first_call_done) {
    return (
      <Card className="border-emerald-500/20 bg-gradient-to-r from-primary/5/30 to-card mb-4" data-testid="first-win-banner">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
              <Trophy size={20} className="text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">First simulation complete!</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Your AI is ready. Make a real call to see it in action with live callers.
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Button size="sm" onClick={() => navigate('/analytics')}
                  className="bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 border border-emerald-500/20 h-7 text-xs px-3"
                  data-testid="first-win-dashboard-btn">
                  <BarChart3 size={12} className="mr-1" /> View Dashboard
                </Button>
                <Badge className="text-[9px] bg-muted text-muted-foreground border-border">
                  {activation_score}% activated
                </Badge>
              </div>
            </div>
            <button onClick={dismiss} className="text-muted-foreground hover:text-muted-foreground p-1" data-testid="dismiss-first-win">
              <X size={14} />
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Nothing done yet — nudge to try
  if (nothingDone) {
    return (
      <Card className="border-sky-500/20 bg-gradient-to-r from-sky-950/20 to-card mb-4" data-testid="activation-nudge-banner">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-sky-500/15 flex items-center justify-center shrink-0">
              <Sparkles size={20} className="text-sky-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">Your AI is ready — try it out!</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Run a quick simulation to see how your AI handles calls, or make a live test call.
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Button size="sm" onClick={() => navigate('/call-simulation')}
                  className="bg-sky-500/15 text-sky-400 hover:bg-sky-500/25 border border-sky-500/20 h-7 text-xs px-3"
                  data-testid="nudge-simulation-btn">
                  <MessageSquare size={12} className="mr-1" /> Run Simulation
                </Button>
                <Button size="sm" variant="ghost" onClick={() => navigate('/calls')}
                  className="text-muted-foreground hover:text-foreground h-7 text-xs px-3"
                  data-testid="nudge-call-btn">
                  <Phone size={12} className="mr-1" /> Make a Call
                </Button>
              </div>
            </div>
            <button onClick={dismiss} className="text-muted-foreground hover:text-muted-foreground p-1" data-testid="dismiss-nudge">
              <X size={14} />
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
}

export function EmptyDashboardState({ type = 'dashboard' }) {
  const navigate = useNavigate();

  const configs = {
    dashboard: {
      title: 'Your dashboard is warming up',
      subtitle: 'Make your first test call or run a simulation to see results here.',
      actions: [
        { label: 'Run a Simulation', icon: MessageSquare, to: '/call-simulation', color: 'emerald' },
        { label: 'View AI Trainer', icon: BarChart3, to: '/call-simulation', color: 'sky' },
      ],
    },
    analytics: {
      title: 'No outcome data yet',
      subtitle: 'Your AI performance metrics will appear after your first call or simulation.',
      actions: [
        { label: 'Run a Simulation', icon: MessageSquare, to: '/call-simulation', color: 'emerald' },
        { label: 'Make a Test Call', icon: Phone, to: '/calls', color: 'violet' },
      ],
    },
    pipeline: {
      title: 'Your pipeline is empty',
      subtitle: 'Leads will appear here as your AI captures them from calls.',
      actions: [
        { label: 'Run a Simulation', icon: MessageSquare, to: '/call-simulation', color: 'emerald' },
      ],
    },
  };

  const cfg = configs[type] || configs.dashboard;

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4" data-testid={`empty-state-${type}`}>
      <div className="h-16 w-16 rounded-2xl bg-muted border border-border flex items-center justify-center mb-4">
        <BarChart3 size={28} className="text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-1">{cfg.title}</h3>
      <p className="text-sm text-muted-foreground text-center max-w-sm mb-5">{cfg.subtitle}</p>
      <div className="flex items-center gap-2">
        {cfg.actions.map((a, i) => {
          const Icon = a.icon;
          const colorMap = {
            emerald: 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 border-emerald-500/20',
            sky: 'bg-sky-500/15 text-sky-400 hover:bg-sky-500/25 border-sky-500/20',
            violet: 'bg-violet-500/15 text-violet-400 hover:bg-violet-500/25 border-violet-500/20',
          };
          return (
            <Button key={i} size="sm" onClick={() => navigate(a.to)}
              className={`${colorMap[a.color]} border h-8 text-xs px-4`}
              data-testid={`empty-action-${i}`}>
              <Icon size={13} className="mr-1.5" /> {a.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

export function PageHint({ id, text, type = 'info' }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const key = `a9_hint_${id}`;
    if (!localStorage.getItem(key)) {
      setVisible(true);
    }
  }, [id]);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(`a9_hint_${id}`, 'true');
  };

  const styles = {
    info: 'border-sky-500/15 bg-sky-500/5 text-sky-400',
    tip: 'border-amber-500/15 bg-amber-500/5 text-amber-400',
    upgrade: 'border-violet-500/15 bg-violet-500/5 text-violet-400',
  };

  return (
    <div className={`flex items-center justify-between gap-3 px-3.5 py-2 rounded-lg border ${styles[type]} mb-3`} data-testid={`hint-${id}`}>
      <p className="text-xs">{text}</p>
      <button onClick={dismiss} className="opacity-50 hover:opacity-100 shrink-0"><X size={12} /></button>
    </div>
  );
}

export function UpgradeNudge({ text, compact = false }) {
  if (compact) {
    return (
      <Badge className="text-[8px] bg-violet-500/8 text-violet-400 border-violet-500/15" data-testid="upgrade-nudge">
        {text}
      </Badge>
    );
  }
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-violet-500/15 bg-violet-500/5 mt-2" data-testid="upgrade-nudge">
      <Sparkles size={12} className="text-violet-400 shrink-0" />
      <p className="text-[11px] text-violet-300">{text}</p>
    </div>
  );
}
